import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PartyPopper, Share2, Check, Trophy, Flame, Users, Music } from 'lucide-react';
import Avatar from './Avatar';

// The post-party recap: the engagement payoff for the room and the
// growth loop for JukeHive — one shareable artifact of the night.
const RecapModal = ({ session, onClose }) => {
  const [copied, setCopied] = useState(false);

  const recap = useMemo(() => {
    const playlist = Object.entries(session.playlist || {});
    const votes = session.votes || {};
    const reactions = session.songReactions || {};

    const netVotes = (key) =>
      Object.entries(votes[key] || {})
        .filter(([uid]) => !uid.startsWith('_'))
        .reduce((sum, [, v]) => sum + (v === 1 ? 1 : v === -1 ? -1 : 0), 0);

    const topSongs = playlist
      .filter(([, s]) => Boolean(s))
      .map(([key, s]) => ({
        name: s.name,
        artist: s.artist,
        image: s.image,
        votes: netVotes(key),
        fires: Object.keys(reactions[key] || {}).length
      }))
      .sort((a, b) => b.votes - a.votes || b.fires - a.fires)
      .slice(0, 3);

    const contributions = {};
    playlist.forEach(([, s]) => {
      const by = s?.addedBy;
      if (!by?.uid || by.uid === session.hostId) return;
      contributions[by.uid] = contributions[by.uid] || { uid: by.uid, name: by.displayName || 'Guest', count: 0 };
      contributions[by.uid].count += 1;
    });
    const mvp = Object.values(contributions).sort((a, b) => b.count - a.count)[0] || null;

    return {
      topSongs,
      mvp,
      guests: Math.max(Object.keys(session.participants || {}).length - 1, 0),
      songsPlayed: playlist.filter(([, s]) => s?.status === 'played' || s?.status === 'playing').length,
      fires: Object.values(reactions).reduce((sum, per) => sum + Object.keys(per || {}).length, 0)
    };
  }, [session]);

  const shareText = [
    `We ran the aux with JukeHive 🎶`,
    `"${session.name}" — ${recap.songsPlayed} songs, ${recap.guests} guests${recap.fires ? `, ${recap.fires} 🔥` : ''}.`,
    recap.topSongs[0] ? `Top song: ${recap.topSongs[0].name} — ${recap.topSongs[0].artist}` : null,
    `Host your own: ${window.location.origin}`
  ].filter(Boolean).join('\n');

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `${session.name} — the recap`, text: shareText });
        return;
      }
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(shareText);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard unavailable */ }
      }
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Party recap"
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="bg-gray-800 rounded-2xl w-full max-w-md p-6 border border-white/10"
      >
        <div className="text-center mb-5">
          <PartyPopper className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
          <h2 className="text-2xl font-bold text-white">That's a wrap!</h2>
          <p className="text-gray-400 text-sm mt-1">"{session.name}" is in the books — and on your record.</p>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-5 text-center">
          <div className="bg-gray-700/60 rounded-xl py-3">
            <Music className="w-4 h-4 text-purple-300 mx-auto mb-1" />
            <p className="text-xl font-bold text-white">{recap.songsPlayed}</p>
            <p className="text-[11px] text-gray-400">songs</p>
          </div>
          <div className="bg-gray-700/60 rounded-xl py-3">
            <Users className="w-4 h-4 text-purple-300 mx-auto mb-1" />
            <p className="text-xl font-bold text-white">{recap.guests}</p>
            <p className="text-[11px] text-gray-400">guests</p>
          </div>
          <div className="bg-gray-700/60 rounded-xl py-3">
            <Flame className="w-4 h-4 text-orange-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-white">{recap.fires}</p>
            <p className="text-[11px] text-gray-400">fires</p>
          </div>
        </div>

        {recap.topSongs.length > 0 && (
          <div className="mb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Top songs of the night</p>
            <div className="space-y-2">
              {recap.topSongs.map((song, i) => (
                <div key={`${song.name}-${i}`} className="flex items-center gap-3 bg-gray-700/40 rounded-lg p-2">
                  <span className="text-sm font-bold text-gray-500 w-4 text-center">{i + 1}</span>
                  {song.image && (
                    <img src={song.image} alt="" className="w-9 h-9 rounded object-cover" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white truncate">{song.name}</p>
                    <p className="text-xs text-gray-400 truncate">{song.artist}</p>
                  </div>
                  {song.votes > 0 && (
                    <span className="text-xs text-green-400 font-semibold shrink-0">▲{song.votes}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {recap.mvp && (
          <div className="flex items-center gap-3 bg-yellow-500/10 border border-yellow-500/25 rounded-lg p-3 mb-5">
            <Trophy className="w-5 h-5 text-yellow-400 shrink-0" />
            <Avatar uid={recap.mvp.uid} name={recap.mvp.name} size="sm" />
            <p className="text-sm text-gray-200 min-w-0">
              <span className="font-semibold text-white">{recap.mvp.name}</span> brought the most songs ({recap.mvp.count})
            </p>
          </div>
        )}

        <button onClick={share} className="btn-primary w-full flex items-center justify-center mb-2">
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
          {copied ? 'Copied!' : 'Share the recap'}
        </button>
        <button
          onClick={onClose}
          className="w-full py-2.5 text-sm text-gray-400 hover:text-white transition-colors"
        >
          Done
        </button>
      </motion.div>
    </div>
  );
};

export default RecapModal;
